import { createReducer, on } from '@ngrx/store';
import { accountLedgerActions } from '../actions/account-ledger.actions';
import { IAccountLedgerPage, ILedgerRowWire } from '../models/account-ledger.model';

export const ACCOUNT_LEDGER_FEATURE_KEY = 'abcpAccountLedger';

export interface AccountLedgerState {
    projectId: string | null;
    accountId: string | null;
    /** The latest page's header: currencies, period, opening and closing. */
    page: Omit<IAccountLedgerPage, 'rows'> | null;
    /** Every row loaded so far, across pages. */
    rows: ILedgerRowWire[];
    loading: boolean;
    loadingMore: boolean;
    error: string | null;
}

export const initialAccountLedgerState: AccountLedgerState = {
    projectId: null,
    accountId: null,
    page: null,
    rows: [],
    loading: false,
    loadingMore: false,
    error: null,
};

const isCurrent = (state: AccountLedgerState, a: { projectId: string; accountId: string }): boolean =>
    state.projectId === a.projectId && state.accountId === a.accountId;

const headerOf = ({ rows: _rows, ...page }: IAccountLedgerPage): Omit<IAccountLedgerPage, 'rows'> => page;

export const accountLedgerReducer = createReducer(
    initialAccountLedgerState,
    // A load REPLACES: it is a new account, or the same one after a sync.
    on(accountLedgerActions.load, (state, { projectId, accountId }) => ({
        ...initialAccountLedgerState,
        projectId,
        accountId,
        // Keeps the current rows while the same account reloads, so the table does not flash.
        rows: isCurrent(state, { projectId, accountId }) ? state.rows : [],
        page: isCurrent(state, { projectId, accountId }) ? state.page : null,
        loading: true,
    })),
    on(accountLedgerActions.loadSuccess, (state, action) =>
        isCurrent(state, action)
            ? { ...state, page: headerOf(action.page), rows: action.page.rows, loading: false, error: null }
            : state,
    ),
    on(accountLedgerActions.loadFailure, (state, action) =>
        isCurrent(state, action) ? { ...state, loading: false, error: action.error } : state,
    ),
    // Load-more APPENDS.
    on(accountLedgerActions.loadMore, (state, action) =>
        isCurrent(state, action) ? { ...state, loadingMore: true } : state,
    ),
    on(accountLedgerActions.loadMoreSuccess, (state, action) =>
        isCurrent(state, action)
            ? { ...state, page: headerOf(action.page), rows: [...state.rows, ...action.page.rows], loadingMore: false }
            : state,
    ),
    on(accountLedgerActions.loadMoreFailure, (state, action) =>
        isCurrent(state, action) ? { ...state, loadingMore: false, error: action.error } : state,
    ),
);
